import { useState } from 'react';
import { Link, useParams } from 'react-router';
import clsx from 'clsx';
import Icon from '../../components/Icon.jsx';
import styles from './DeckTree.module.scss';

function useCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('recall.collapsed') || '[]'));
    } catch {
      return new Set();
    }
  });
  const toggle = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      localStorage.setItem('recall.collapsed', JSON.stringify([...next]));
      return next;
    });
  };
  return [collapsed, toggle];
}

export function dueTotal(counts) {
  if (!counts) return 0;
  return counts.new + counts.learn + counts.review;
}

/** One calm number: how many cards are waiting today. */
export function DueBadge({ counts, compact }) {
  const n = dueTotal(counts);
  if (!counts) return null;
  if (!n) {
    if (compact) return null;
    return (
      <span className={clsx(styles.badge, styles.done)} aria-label="Nothing to study today">
        <Icon name="check" size={16} stroke={3} />
      </span>
    );
  }
  return (
    <span
      className={clsx(styles.badge, compact && styles.badgeCompact)}
      aria-label={`${n} to study: ${counts.new} new, ${counts.learn} learning, ${counts.review} review`}
    >
      {n}
      {compact ? null : <small>to study</small>}
    </span>
  );
}

function subtitle(counts, childCount) {
  const parts = [];
  if (counts?.total != null) parts.push(`${counts.total} card${counts.total === 1 ? '' : 's'}`);
  if (childCount) parts.push(`${childCount} subdeck${childCount === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

function Node({ node, depth, counts, collapsed, toggle, compact, activeId }) {
  const hasChildren = node.children.length > 0;
  const isCollapsed = collapsed.has(node.id);
  const c = counts?.get(node.id);
  return (
    <li>
      <div
        className={clsx(styles.row, activeId === node.id && styles.active)}
        style={{ '--depth': depth }}
      >
        <Link to={`/deck/${node.id}`} className={styles.name}>
          <span className={styles.nameText}>{node.name}</span>
          {compact ? null : <span className={styles.sub}>{subtitle(c, node.children.length)}</span>}
        </Link>
        <DueBadge counts={c} compact={compact} />
        {hasChildren ? (
          <button
            type="button"
            className={clsx(styles.twisty, isCollapsed && styles.closed)}
            onClick={() => toggle(node.id)}
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? `Show subdecks of ${node.name}` : `Hide subdecks of ${node.name}`}
          >
            <Icon name="down" size={18} stroke={2.4} />
          </button>
        ) : compact ? null : (
          <Icon name="chevron" size={18} className={styles.chev} />
        )}
      </div>
      {hasChildren && !isCollapsed ? (
        <ul className={styles.list}>
          {node.children.map((ch) => (
            <Node
              key={ch.id}
              node={ch}
              depth={depth + 1}
              counts={counts}
              collapsed={collapsed}
              toggle={toggle}
              compact={compact}
              activeId={activeId}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export default function DeckTree({ tree, counts, compact = false }) {
  const [collapsed, toggle] = useCollapsed();
  const { deckId } = useParams();
  if (!tree.length) return null;
  return (
    <ul className={clsx(styles.list, styles.root, compact && styles.compact)}>
      {tree.map((n) => (
        <Node
          key={n.id}
          node={n}
          depth={0}
          counts={counts}
          collapsed={collapsed}
          toggle={toggle}
          compact={compact}
          activeId={deckId}
        />
      ))}
    </ul>
  );
}
